import { Box } from '@mui/material'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { getUserNotifications } from 'api/users'
import columnRenderers, { PLACEHOLDER } from 'components/column-renderers'
import dateRenderer from 'components/column-renderers/date'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import { getActionColumn, RowAction } from 'features/UI'
import { useGridControl } from 'hooks/useGridControl'
import { useCallback, useMemo, useState } from 'react'
import { BaseTableRequest, UserNotification } from 'types'
import { stripHtmlTags, useGetDataSource } from 'utils'
import { UserNotificationMessageModal } from './UserNotificationMessageModal'

export function UserNotifications({ userId }: { userId: number }) {
  const getNotificationsCallback = useCallback(
    (params: BaseTableRequest) => {
      const sort = params.sort || { field: 'updatedAt', sort: 'desc' }
      return getUserNotifications({
        ...params,
        filter: {
          ...(params.filter || {}),
        },
        sort,
        userId,
      })
    },
    [userId],
  )

  const getDataSource = useGetDataSource(getNotificationsCallback)

  const { gridRef, onGridReady } = useGridControl(getDataSource)

  const [selectedNotification, setSelectedNotification] = useState<UserNotification | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleViewMessage = useCallback(
    (id: number, cellProps: ICellRendererParams<UserNotification>) => {
      if (cellProps.data) {
        setSelectedNotification(cellProps.data)
        setIsModalOpen(true)
      }
    },
    [],
  )

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setSelectedNotification(null)
  }, [])

  const actions: RowAction[] = useMemo(() => {
    return [
      {
        name: 'View message',
        onClick: handleViewMessage,
      },
    ]
  }, [handleViewMessage])

  const columnDefs: ColDef[] = useMemo(() => {
    return [
      {
        headerName: 'Notification',
        field: 'message',
        cellRenderer: (props: ICellRendererParams<UserNotification>) => {
          if (!props.data) return PLACEHOLDER

          const { message } = props.data
          return stripHtmlTags(message) || PLACEHOLDER
        },
      },
      {
        headerName: 'Date',
        field: 'sentAt',
        cellRenderer: dateRenderer('sentAt'),
        maxWidth: 240,
      },
      {
        headerName: 'Notification Delivery',
        field: 'deliveredVia',
        cellRenderer: columnRenderers.capitalizeRenderer('deliveredVia'),
        maxWidth: 240,
      },
      { ...getActionColumn(actions) },
    ]
  }, [actions])

  return (
    <>
      <Box mt={4} />
      <InfinityAgGrid
        pagination
        innerRef={gridRef}
        columnDefs={columnDefs}
        onGridReady={onGridReady}
      />

      <UserNotificationMessageModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        notification={selectedNotification}
      />
    </>
  )
}
